import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, CATEGORY_META, fmtCurrency, fmtMarketCap, fmtPct } from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowLeft, Star } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { toast } from "sonner";

export default function StockDetail() {
    const { ticker } = useParams();
    const [stock, setStock] = useState(null);
    const [watched, setWatched] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [s, wl] = await Promise.all([
                    api.get(`/stocks/${ticker}`),
                    api.get(`/watchlist`).catch(() => ({ data: { items: [] } })),
                ]);
                setStock(s.data);
                setWatched((wl.data.items || []).some((x) => x.ticker === ticker.toUpperCase()));
            } catch {
                toast.error("Stock not found");
            }
        })();
    }, [ticker]);

    const toggleWatch = async () => {
        try {
            if (watched) {
                await api.delete(`/watchlist/${ticker}`);
                setWatched(false);
                toast.success(`Removed ${ticker} from watchlist`);
            } else {
                await api.post("/watchlist", { ticker });
                setWatched(true);
                toast.success(`Added ${ticker} to watchlist`);
            }
        } catch {
            toast.error("Could not update watchlist");
        }
    };

    if (!stock) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const cm = CATEGORY_META[stock.category];
    const history = stock.dividend_history || [];

    return (
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
            <Link to="/dashboard" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground gap-1.5 mb-6" data-testid="back-link">
                <ArrowLeft className="h-4 w-4" />
                Back to screener
            </Link>

            {/* HEADER */}
            <div className="flex flex-wrap items-start justify-between gap-6 mb-10 animate-fade-up">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight" data-testid="stock-ticker">
                            {stock.ticker}
                        </h1>
                        <span className={`inline-flex text-xs font-medium px-2 py-1 rounded-md ${cm?.cls}`}>
                            {cm?.label} · {cm?.years}
                        </span>
                    </div>
                    <div className="mt-2 text-lg text-muted-foreground">{stock.name}</div>
                    <div className="mt-1 text-sm text-muted-foreground">{stock.sector} · {stock.industry}</div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <div className="overline">Price</div>
                        <div className="font-heading text-2xl font-bold tabular-nums">{fmtCurrency(stock.price)}</div>
                    </div>
                    <Button
                        variant={watched ? "default" : "outline"}
                        onClick={toggleWatch}
                        className="rounded-full"
                        data-testid="watch-btn"
                    >
                        <Star className={`h-4 w-4 ${watched ? "fill-current" : ""}`} />
                        {watched ? "Watching" : "Add to watchlist"}
                    </Button>
                </div>
            </div>

            {/* KEY METRICS */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
                <Metric label="Yield"          value={fmtPct(stock.yield)} primary />
                <Metric label="Growth streak"  value={`${stock.growth_streak} years`} primary />
                <Metric label="Payout ratio"   value={fmtPct(stock.payout_ratio, 0)} />
                <Metric label="P/E"            value={stock.pe?.toFixed(1)} />
                <Metric label="Market cap"     value={fmtMarketCap(stock.market_cap)} />
                <Metric label="5Y CAGR"        value={fmtPct(stock.cagr_5y, 1)} />
            </div>

            {/* CHARTS */}
            <div className="grid lg:grid-cols-3 gap-6 mb-10">
                <div className="lg:col-span-2 rounded-2xl border border-border bg-background p-6">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <div className="overline">Dividend history</div>
                            <div className="font-heading text-xl font-semibold mt-1">Annual payout per share</div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {history.length > 0 && `${history[0].year} – ${history[history.length - 1].year}`}
                        </div>
                    </div>
                    <div className="h-72 mt-4 min-h-[280px] w-full" data-testid="dividend-chart">
                        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={280}>
                            <BarChart data={history} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                                <CartesianGrid stroke="#E5E7EB" strokeDasharray="2 4" vertical={false} />
                                <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                                <Tooltip
                                    contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 12 }}
                                    formatter={(v) => [`$${Number(v).toFixed(2)}`, "Dividend"]}
                                />
                                <Bar dataKey="amount" fill="#059669" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="rounded-2xl border border-border bg-background p-6">
                    <div className="overline">Dividend growth rates</div>
                    <div className="font-heading text-xl font-semibold mt-1">DGR by period</div>
                    <div className="h-72 mt-4 min-h-[280px] w-full">
                        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={280}>
                            <LineChart
                                data={[
                                    { period: "1Y",  value: stock.dgr_1y },
                                    { period: "3Y",  value: stock.dgr_3y },
                                    { period: "5Y",  value: stock.dgr_5y },
                                    { period: "10Y", value: stock.dgr_10y },
                                ]}
                                margin={{ top: 8, right: 8, left: -8, bottom: 0 }}
                            >
                                <CartesianGrid stroke="#E5E7EB" strokeDasharray="2 4" vertical={false} />
                                <XAxis dataKey="period" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                                <Tooltip
                                    contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 12 }}
                                    formatter={(v) => [`${Number(v).toFixed(1)}%`, "DGR"]}
                                />
                                <Line type="monotone" dataKey="value" stroke="#059669" strokeWidth={2.5} dot={{ r: 4, fill: "#059669" }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Metric({ label, value, primary }) {
    return (
        <div className={`rounded-xl border border-border p-4 ${primary ? "bg-accent" : "bg-background"}`}>
            <div className="overline">{label}</div>
            <div className={`mt-2 font-heading text-xl font-bold tabular-nums ${primary ? "text-accent-foreground" : ""}`}>
                {value}
            </div>
        </div>
    );
}
